import { fokaAxios } from 'app/fokaAxios'
import Limitations from 'features/Limitations'
import { CampaignTypes } from 'features/Codes'

const { get, post, put, patch } = fokaAxios

const CampaignTypePaths = {
  [CampaignTypes.PREMIUM_LINK]: 'premiumlink',
  [CampaignTypes.TALK_CHANNEL]: 'talkchannel',
}

export default {
  getBizChannels: () =>
    get('/api/v2/biz-channels', {
      params: {
        limit: Limitations.MAX_BIZ_CHANNELS,
      },
    }),
  getBizChannel: bizChannelId => get(`/api/v2/biz-channels/${bizChannelId}`),
  getCampaign: campaignId => get(`/api/v2/campaigns/${campaignId}`),
  getCampaigns: () => get('/api/v2/campaigns'),
  getNotDeletedCampaigns: () => get('/api/v2/campaigns?notDeleted=true'),
  getBizChannelCampaigns: bizChannelId =>
    get(`/api/v2/campaigns/bizchannels/${bizChannelId}`),
  getBizChannelNotDeletedCampaigns: bizChannelId =>
    get(`/api/v2/campaigns/bizchannels/${bizChannelId}?notDeleted=true`),
  getCampaignGenerable: (type = CampaignTypes.PREMIUM_LINK) =>
    get('/api/v2/campaigns/generable', {
      params: { type },
    }),
  postCampaign: ({ campaignType, ...createCampaignCommand }) =>
    post(
      `/api/v2/campaigns/${CampaignTypePaths[campaignType]}`,
      createCampaignCommand
    ),
  putCampaign: ({ campaignId, campaignType, ...updateCampaignCommand }) =>
    put(
      `/api/v2/campaigns/${CampaignTypePaths[campaignType]}/${campaignId}`,
      updateCampaignCommand
    ),
  getAdGroup: adGroupId => get(`/api/v2/adgroups/${adGroupId}`),
  getAdGroupGenerable: campaignId =>
    get('/api/v2/adgroups/generable', {
      params: { campaignId },
    }),
  getCampaignAdGroups: campaignId =>
    get(`/api/v2/adgroups/campaigns/${campaignId}`),
  getCampaignNotDeletedAdGroups: campaignId =>
    get(`/api/v2/adgroups/campaigns/${campaignId}?notDeleted=true`),
  getCampaignsById: campaignIds =>
    get('/api/v2/campaigns/multi', {
      params: {
        campaignIds: campaignIds.join(','),
      },
    }),
  getAdGroups: adGroupIds =>
    get('/api/v2/adgroups', {
      params: {
        adGroupIds: adGroupIds.join(','),
      },
    }),
  postAdGroup: (campaignType, createAdGroupCommand) =>
    post(
      `/api/v2/adgroups/${CampaignTypePaths[campaignType]}`,
      createAdGroupCommand
    ),
  putAdGroup: (campaignType, updateAdGroupCommand) =>
    put(
      `/api/v2/adgroups/${CampaignTypePaths[campaignType]}`,
      updateAdGroupCommand
    ),
  copyAdGroups: copyAdGroupsCommand =>
    post('/api/v2/adgroups/copy', copyAdGroupsCommand),
  copyableAdGroups: adGroupIds =>
    post('/api/v2/adgroups/copy/list/possible', {
      adGroupIds,
    }),
  addPremiumlinkKeywords: createKeywordCommand =>
    post('/api/v2/keywords/premiumlink', createKeywordCommand),
  postKeywords: (campaignType, createKeywordCommand) =>
    post(
      `/api/v2/keywords/${CampaignTypePaths[campaignType]}${
        campaignType === CampaignTypes.PREMIUM_LINK ? '/individual' : ''
      }`,
      createKeywordCommand
    ),
  copyKeywords: copyKeywordsCommand =>
    post('/api/v2/keywords/copy', copyKeywordsCommand),
  getAdGroupKeywords: adGroupId =>
    get(`/api/v2/keywords/adgroups/${adGroupId}`),
  getNotDeletedKeywords: adGroupId =>
    get(`/api/v2/keywords/adgroups/${adGroupId}?notDeleted=true`),
  getKeyword: keywordId => get(`/api/v2/keywords/${keywordId}`),
  getKeywords: keywordIds =>
    get('/api/v2/keywords', {
      params: {
        keywordIds: keywordIds.join(','),
      },
    }),
  getKeywordsGenerable: adGroupId =>
    get('/api/v2/keywords/generable', {
      params: {
        adGroupId,
      },
    }),
  getCreativeLinksGenerable: adGroupId =>
    get('/api/v2/creativelinks/generable', {
      params: {
        adGroupId,
      },
    }),
  getCreativeLink: creativeLinkId =>
    get(`/api/v2/creativelinks/${creativeLinkId}`),
  patchAdGroupsBiddingStrategy: ({
    adGroupIds,
    changeMaxBiddingAmount,
    maxBiddingAmount,
    changeContentMatchingMaxBiddingAmount,
    customContentMatchingMaxBiddingAmountYn,
    contentMatchingMaxBiddingAmount,
    changePcWeight,
    pcWeight,
    changeMobileWeight,
    mobileWeight,
    changeKeywordExtensionWeight,
    keywordExtensionWeight,
  }) =>
    fokaAxios.patch('/api/v2/adgroups/biddingstrategy', {
      adGroupIds,
      changeMaxBiddingAmount,
      maxBiddingAmount,
      changeContentMatchingMaxBiddingAmount,
      customContentMatchingMaxBiddingAmountYn,
      ...(customContentMatchingMaxBiddingAmountYn === 'Y' && {
        contentMatchingMaxBiddingAmount,
      }),
      changePcWeight,
      pcWeight,
      changeMobileWeight,
      mobileWeight,
      changeKeywordExtensionWeight,
      keywordExtensionWeight,
    }),
  postCreativeLinks: (adGroupId, creativeIds) =>
    post('/api/v2/creativelinks', {
      adGroupId,
      creativeIds,
    }),
  getCreativeLinksByAdGroup: adGroupId =>
    get(`/api/v2/creativelinks/adgroups/${adGroupId}`),
  getBizWalletsSpendInfo: () => get('/api/v2/biz-wallets/spend-info'),
  patchCampaignsDailyBudget: ({
    campaignIds,
    dailyBudgetYn,
    dailyBudgetAmount,
  }) =>
    patch('/api/v2/campaigns/dailybudget', {
      campaignIds,
      dailyBudgetYn,
      dailyBudgetAmount,
    }),
  patchCampaignsConversionTracking: ({
    campaignIds,
    kakaoAccountId,
    conversionTrackingId,
  }) =>
    patch('/api/v2/campaigns/conversiontracking', {
      campaignIds,
      kakaoAccountId,
      conversionTrackingId,
    }),
  patchCampaignsTraceUrl: ({ campaignIds, traceUrlYn }) =>
    patch('/api/v2/campaigns/traceurl', {
      campaignIds,
      traceUrlYn,
    }),
  patchAdGroupsPlacement: ({
    adGroupIds,
    placementType,
    placementCategories,
  }) =>
    patch('/api/v2/adgroups/placement', {
      adGroupIds,
      placementType,
      placementCategories,
    }),
  patchAdGroupsDevice: ({ adGroupIds, deviceSettingType, deviceTypes }) =>
    patch('/api/v2/adgroups/device', {
      adGroupIds,
      deviceSettingType,
      deviceTypes,
    }),
  patchAdGroupsDuration: ({ adGroupIds, startDate, endDate }) =>
    patch('/api/v2/adgroups/impression/duration', {
      adGroupIds,
      startDate,
      endDate,
    }),
  patchAdGroupsWeeklySchedule: ({ adGroupIds, weeklySchedule }) =>
    patch('/api/v2/adgroups/impression/weeklySchedule', {
      adGroupIds,
      weeklySchedule,
    }),
  patchAdGroupsDailyBudget: ({ adGroupIds, dailyBudgetAmount }) =>
    patch('/api/v2/adgroups/dailybudget', {
      adGroupIds,
      dailyBudgetAmount,
    }),
  patchAdGroupsKeywordExtension: ({
    adGroupIds,
    keywordExtensionYn,
    exceptionKeywords,
  }) =>
    patch('/api/v2/adgroups/keywordExtension', {
      adGroupIds,
      keywordExtensionYn,
      exceptionKeywords,
    }),
  getKeywordReviewReason: keywordId =>
    get(`/api/v2/keywords/${keywordId}/review/reason`),
}
